import { useState } from 'react';
import { NJButton, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import { getMapboxToken, UMSelect, UMSegmentedControl } from '../../urbanmap/_components';
import { CATEGORIES, ROADS } from './data';
import { effectiveRule } from './mocks';
import { RuleEditor } from './RuleEditor';
import { NameField } from './NameField';
import type { Hypotheses, MapTool, NetworkKind, Scenario } from './types';
import { DecisionNote } from './DecisionPanel';

export function HypothesesPanel({ scenario, selectedRoad, selectRoad, save, rename, setTool, networkKind, onDecision }: {
  scenario: Scenario; selectedRoad: string; selectRoad: (id: string) => void;
  save: (h: Hypotheses) => void; rename: (name: string) => void;
  setTool: (tool: MapTool) => void; networkKind: NetworkKind;
  onDecision: (id: string) => void;
}) {
  const [tab, setTab] = useState('Routes');
  const mapAvailable = Boolean(getMapboxToken());
  const h = scenario.hypotheses;
  const road = ROADS.find(r => r.id === selectedRoad)!;
  const local = h.local[road.id];
  const rule = effectiveRule(h, road);
  return <section className="sp-panel sp-stack">
    <NJHeading as="h2" scale="md">Jeu d’hypothèses</NJHeading>
    <NameField label="Nom du scénario" value={scenario.name} save={rename} />
    <NJText scale="sm">Ces règles servent à générer les plans. Leur modification n’invalide pas les plans déjà calculés.</NJText>
    <UMSegmentedControl options={['Routes', 'Départs']} value={tab} onChange={setTab} />
    <div hidden={tab !== 'Routes'}>
      <NJHeading as="h3" scale="sm">Règles par type de route</NJHeading>
      <DecisionNote id="catalog" onOpen={onDecision} />
      <NJText scale="sm">1 = référence · &gt; 1 défavorise · &lt; 1 favorise. Aucun coût financier.</NJText>
      {CATEGORIES.map(category => <RuleEditor key={category}
        label={category} rule={h.categories[category]} onSave={value => save({
          ...h, categories: { ...h.categories, [category]: value },
        })} />)}
      <div className="sp-divider" />
      <NJHeading as="h3" scale="sm">Exception sur un tronçon</NJHeading>
      <NJText scale="sm">Cliquez sur la carte ou choisissez un tronçon. La règle locale prime toujours sur son type.</NJText>
      <UMSelect ariaLabel="Tronçon sélectionné" value={road.id} onChange={selectRoad}
        options={ROADS.map(r => ({ value: r.id, label: r.name }))} />
      <div className="sp-notice">
        <NJText as="div" scale="sm">{road.category} · {local ? 'Règle locale prioritaire' : 'Hérite de la règle du type'}</NJText>
      </div>
      <RuleEditor key={road.id} label={road.name} local rule={rule}
        onSave={value => save({ ...h, local: { ...h.local, [road.id]: value } })} />
      {local && <NJButton label="Rétablir la règle du type" scale="sm" emphasis="minimal" onClick={() => {
        const next = { ...h.local }; delete next[road.id]; save({ ...h, local: next });
      }} />}
      <NJText scale="sm">Forcé = inclusion intégrale du tronçon. Interdit = passage exclu. Trois modes locaux exclusifs.</NJText>
    </div>
    <div hidden={tab !== 'Départs'}>
      <NJHeading as="h3" scale="sm">Départs possibles</NJHeading>
      <NJText>Le calcul peut choisir parmi ces départs ; il n’est pas obligé de tous les utiliser.</NJText>
      {networkKind === 'existing' && <NJButton label="Accrocher le réseau existant" icon="add_location_alt" scale="sm"
        emphasis="subtle" disabled={!mapAvailable} onClick={() => setTool('network')} />}
      <NJButton label="Placer une centrale" icon="add_location" scale="sm" disabled={!mapAvailable} onClick={() => setTool('plant')} />
      {!mapAvailable && <NJText as="div" role="status">Un token Mapbox valide est nécessaire pour ajouter un départ sur la carte.</NJText>}
      <NJText scale="sm">Centrale : placez un point, puis reliez-le à une route. Réseau : le snapping retient le tronçon accroché.</NJText>
      {!h.departures.length && <NJText role="status">Aucun départ. Ajoutez-en un pour générer un plan.</NJText>}
      <ul className="sp-list">{h.departures.map(d => <li className="sp-card" key={d.id}>
        <NJText as="div"><strong>{d.label}</strong></NJText>
        <NJText as="div" scale="sm">{d.kind === 'plant' ? 'Centrale et liaison à la route' :
          `Tronçon existant · ${networkKind === 'new' ? 'non utilisé dans un réseau nouveau' : 'accroché'}`}</NJText>
        <NJButton label={`Retirer ${d.label}`} scale="sm" emphasis="minimal" onClick={() => save({
          ...h, departures: h.departures.filter(item => item.id !== d.id),
        })} />
      </li>)}</ul>
    </div>
  </section>;
}
